/**
 * "Mis reservas" — the verified customer's bookings across EVERY salon
 * (#106, cross-salon union #114 / vitrina-gae-cdn#57).
 *
 * One call: the edge's GET /api/booking/my (vitrina-gae-cdn#55/#57). The
 * session is the contact token, sent the dual-store way (the cookie rides
 * same-origin automatically; the localStorage copy travels in Authorization
 * so an ITP cookie purge alone doesn't log the customer out), plus the GDID
 * header the token's binding is recomputed against.
 *
 * `scope`: "active" (>= 48h ago, what the menu polls) or "history" (older,
 * loaded once when the Historial disclosure expands). The exact salon-local
 * DATE split is this client's job — see utils/bookingDates.
 *
 * Answer contract, normalized for the composable:
 *   { ok: true,  bookings: [...], salons: {sid: {snm, tz, wa}} }
 *   { ok: false, reason: "reverify" | "no-session" | "unavailable" }
 * "reverify" is the edge's 401 for a REAL identity whose last verification is
 * older than the history tier (7 days) — the caller offers the one-tap
 * re-verification instead of the list.
 */

import { getVitrinaApiBase } from '@/js/utils/environment';
import { gdidHeader, recallContactToken } from '@/js/apis/contactToken';

/**
 * The answer is bound to the token that asked (Codex on #138, round 6): the
 * token can be REPLACED while a request is in the air — another tab logs out
 * and mints the next customer's — and then a late success would install the
 * previous customer's bookings under the new identity, while a late 401 would
 * forget the NEW token. So the token is read once more when the answer lands;
 * if it is no longer the one that asked, the answer is discarded as a local
 * no-session: it invalidates the data without touching the replacement.
 */
export async function fetchMyBookings(salonId, scope = 'active') {
  const token = recallContactToken();
  // No token in this browser: the same 'no-session' the edge would answer,
  // but decided here, without a request — `local` says so, because a dead
  // token (the edge's own 'no-session') is the one that must be forgotten
  // (#136) and an absent one has nothing to forget.
  if (!token) return { ok: false, reason: 'no-session', local: true };
  const result = await fetchWithToken(salonId, scope, token);
  if (recallContactToken() !== token) return { ok: false, reason: 'no-session', local: true };
  return result;
}

async function fetchWithToken(salonId, scope, token) {
  try {
    const response = await fetch(
      `${getVitrinaApiBase()}/api/booking/my?salon=${encodeURIComponent(salonId)}` +
        `&scope=${encodeURIComponent(scope)}`,
      {
        headers: {
          ...gdidHeader(),
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.status === 401) {
      let error = '';
      try {
        error = (await response.json())?.error || '';
      } catch {
        /* body optional */
      }
      return { ok: false, reason: error === 'reverify' ? 'reverify' : 'no-session' };
    }
    if (!response.ok) return { ok: false, reason: 'unavailable' };
    const data = await response.json();
    if (data?.ok !== true) return { ok: false, reason: 'unavailable' };
    return {
      ok: true,
      bookings: data.bookings || [],
      salons: data.salons && typeof data.salons === 'object' ? data.salons : {},
    };
  } catch (error) {
    console.error('[myBookings] fetch failed:', error);
    return { ok: false, reason: 'unavailable' };
  }
}
